import { setProgressBarCurrentStep } from '@avalara/bat-apps-shared/client/actions/progress-bar';
import { isMobileDevice } from '@avalara/bat-apps-shared/helpers/index';
import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { CYBERSOURCE_CANCEL_MESSAGE_TYPE, CYBERSOURCE_SUBMIT_MESSAGE_TYPE } from '@avalara/bat-apps-shared/types/cybersource';
import { getCookie } from '@avalara/bat-apps-shared/helpers/cookies';
import sessionStorage from '@avalara/bat-apps-shared/helpers/sessionStorage';
import conf from '../conf';
import IframePaymentContainer from '../containers/iframe-payment-container';
import { updateProductAnalytics } from '../lib/side-effects/analytics';
import { getCybersourceFormData } from '../lib/side-effects/cybersource';
import { purchaseOffer, addPaidAvataxTier, addReturnsOffer, addRiskAssessmentOffer, } from '../lib/side-effects/offers';
import { stepNames } from '../lib/side-effects/progress-bar';
import { setCurrentOfferPage } from '../store/offer';
import { FormContainer } from './offer/styled/forms';
import { selectState, selectIsNonAdmin } from '../selectors';
import { clearSystemError, setNeedToFetchCybersourceData } from '../store/system';
import { getSystemErrorMessaging } from '../lib/side-effects/error';
import { Pages } from '../../shared/types/offers';
import DefaultPaymentView from '../containers/default-payment-view';
import OfferPaymentView from './offer/forms/payment';
import RiskAssessmentPaymentView from './risk-assessment/payment';
import { RelativeDiv } from './styled/skylab/page';
import { createAvataxPaidAccount, createNetNewSTRAAccount, } from '../lib/side-effects/accounts';
import { goToNextOfferStep } from '../lib/side-effects/offer-flow';
import analytics from '../lib/analytics';
import { addCreditCard } from '../store/account';
const PaymentFormContainer = styled(FormContainer) `
  height: inherit;
`;
const defaultFormData = {
    access_key: '',
    amount: '',
    currency: '',
    locale: '',
    profile_id: '',
    reference_number: '',
    signature: '',
    signed_date_time: '',
    signed_field_names: '',
    transaction_type: '',
    transaction_uuid: '',
    unsigned_field_names: '',
    bill_to_forename: '',
    bill_to_surname: '',
    bill_to_address_country: '',
    bill_to_address_line1: '',
    bill_to_address_postal_code: '',
    bill_to_address_city: '',
    bill_to_address_state: '',
    bill_to_company_name: '',
};
const formDataFields = Object.keys(defaultFormData);
const CybersourcePaymentPage = ({ paymentView, isEmbedded, onSuccess }) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const hiddenForm = useRef(null);
    const { account: { selectedPaymentMethod, isLoggedIn }, buyFlow: { hideIframe: hideBuyFlowIframe }, offer: { offerFlow }, sharedSignup: { signupForm }, system: { activeFlow, needToFetchCybersourceData }, } = useSelector(selectState);
    const [formData, setFormData] = useState(defaultFormData);
    const isNonAdmin = useSelector(selectIsNonAdmin);
    const usingExistingPaymentMethod = !!selectedPaymentMethod;
    const hideIframe = usingExistingPaymentMethod || (isLoggedIn && isNonAdmin) || hideBuyFlowIframe;
    useEffect(() => {
        dispatch(setCurrentOfferPage(Pages.Payment));
        dispatch(setProgressBarCurrentStep(stepNames.paymentMethod));
        dispatch(updateProductAnalytics('purchase_start', true));
    }, []);
    useEffect(() => {
        if (usingExistingPaymentMethod || !needToFetchCybersourceData)
            return;
        dispatch(getCybersourceFormData('USD', signupForm, setFormData));
        dispatch(setNeedToFetchCybersourceData(false));
    }, [needToFetchCybersourceData]);
    useEffect(() => {
        if (activeFlow !== null)
            sessionStorage.set('activeFlow', activeFlow);
    }, [activeFlow]);
    const onAddObligation = (options) => {
        const retrievedActiveFlow = sessionStorage.get('activeFlow');
        switch (retrievedActiveFlow) {
            case 'avatax-buy':
                dispatch(addPaidAvataxTier(options));
                break;
            case 'avatax-direct-buy':
                dispatch(createAvataxPaidAccount(options));
                break;
            case 'returns':
                dispatch(addReturnsOffer(options));
                break;
            case 'risk-assessment-net-new':
                dispatch(createNetNewSTRAAccount(options));
                break;
            case 'risk-assessment':
                dispatch(addRiskAssessmentOffer(options));
                break;
            default:
                break;
        }
    };
    const onAddCreditCard = (paymentMethodToken) => dispatch(addCreditCard(paymentMethodToken));
    const onHandlePaymentError = (message) => dispatch(getSystemErrorMessaging({
        errorCode: 'failedCybersourcePayment',
        msg: message || '',
    }));
    useEffect(() => {
        const callbackListener = (msg) => {
            if ((getCookie('TESTCAFE_E2E'))) {
                if (offerFlow) {
                    dispatch(goToNextOfferStep({ history }));
                }
                else {
                    onAddObligation({ paymentToken: 'test', history });
                }
                return;
            }
            const { message, paymentMethodId, success, type } = msg.data || {};
            if (type === CYBERSOURCE_CANCEL_MESSAGE_TYPE) {
                dispatch(setNeedToFetchCybersourceData(true));
                history.go(-3);
            }
            else if (type === CYBERSOURCE_SUBMIT_MESSAGE_TYPE) {
                if (success) {
                    const paymentToken = paymentMethodId || '';
                    const options = {
                        history,
                        paymentToken,
                        handleError: onHandlePaymentError,
                    };
                    dispatch(clearSystemError());
                    if (paymentView === 'add-credit-card') {
                        onAddCreditCard(paymentToken);
                        if (onSuccess)
                            onSuccess();
                    }
                    else if (paymentView === 'offer' || offerFlow) {
                        onAddCreditCard(paymentToken);
                        if (isEmbedded) {
                            dispatch(goToNextOfferStep({ history }));
                        }
                        else {
                            dispatch(purchaseOffer(history, paymentToken));
                        }
                    }
                    else {
                        onAddObligation(options);
                    }
                }
                else {
                    dispatch(setNeedToFetchCybersourceData(true));
                    dispatch(getSystemErrorMessaging({ errorCode: 'failedCybersourcePayment', msg: message || '' }));
                    const errorData = {
                        error: true,
                        message,
                        errorResources: {
                            cybersource: 'https://developer.cybersource.com/library/documentation/dev_guides/Secure_Acceptance_Hosted_Checkout/html/Topics/Reason_Codes.htm',
                        },
                    };
                    analytics.track('CYBERSOURCE_PAYMENT_SUBMIT_ERROR', errorData);
                }
            }
        };
        window.addEventListener('message', callbackListener);
        return () => window.removeEventListener('message', callbackListener);
    }, []);
    useEffect(() => {
        var _a;
        if (formData.signature)
            (_a = hiddenForm.current) === null || _a === void 0 ? void 0 : _a.submit();
    }, [formData.signature]);
    const cybersourcePageEl = document.getElementById('cybersource-page');
    const paymentFormContainerEl = document.getElementById('payment-form-container');
    if (!isMobileDevice() && cybersourcePageEl && paymentFormContainerEl) {
        cybersourcePageEl.width = `${paymentFormContainerEl.offsetWidth - 90}`;
    }
    const getPaymentView = () => {
        switch (paymentView) {
            case 'offer':
                return (React.createElement(OfferPaymentView, { isEmbedded: isEmbedded }));
            case 'risk-assessment-net-new':
            case 'risk-assessment':
                return React.createElement(RiskAssessmentPaymentView, null);
            case 'add-credit-card':
                return React.createElement(RelativeDiv, null,
                    React.createElement(IframePaymentContainer, { hideIframe: hideIframe, isCybersource: true }));
            default:
                return React.createElement(DefaultPaymentView, null);
        }
    };
    return React.createElement(React.Fragment, null,
        React.createElement("form", { ref: hiddenForm, method: 'post', action: `${conf.cybersourcePageHost}/token/create`, target: 'cybersource-page' }, formDataFields.map(formDataField => (React.createElement("input", { type: 'hidden', key: formDataField, id: formDataField, name: formDataField, defaultValue: formData[formDataField] })))),
        React.createElement(PaymentFormContainer, { id: 'payment-form-container' }, getPaymentView()));
};
export default CybersourcePaymentPage;
